<template>
  <div>
    <b-table :current-page="currentPage"
             :fields="gradeHelpers.gradesTableFields"
             :items="proposalsFromProp"
             :per-page="perPage"
             :sort-compare="sortCompare"
             :tbody-tr-class="highlightOpenedProposals"
             details-td-class="pa-0"
             head-variant="light"
             hover
             stacked="lg"
             striped
             @row-clicked="setShowFinalSubmit">

      <template v-slot:top-row="{fields}">
        <proposal-filter :id="parent"
                         v-model="proposalsFromProp"
                         :fields="fields"
                         :not-include-key="['actions', 'ordinalNumber']"
                         :selected-call="selectedCall"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage: perPage, currentPage: currentPage}"/>
      </template>

      <template v-slot:cell(actions)="row">
        <b-row>
          <b-col :style="{'max-width': '0.3rem'}"
                 cols="1">
            <traffic-lights-buttons v-model="row.item[gradeType].acceptance_mark"
                                    :options="trafficLightsOptions"
                                    disabled
                                    stacked/>
          </b-col>
          <b-col class="default-actions"
                 @click="setShowFinalSubmit(row.item, undefined, $event)"
                 @keyup.down="setShowFinalSubmit(row.item, undefined, $event)">
            <base-action-expand-details :b-table-row="row"
                                        @click="handleDetailsShowing(row)"/>

            <action-download-pdf :proposal="row.item"/>

            <base-material-design-icon-button v-b-modal.gradeModal
                                              :title="isEditionAllowed(row.item) ? 'Edit grade' : 'View grade'"
                                              icon="fact_check"
                                              @click="setProposal(row.item)"/>

            <action-open-proposal-full-details :proposal="row.item"/>

            <base-double-click-submit-button v-if="isSubmissionAllowed(row.item)"
                                             :btn-options="submitBtnOptions"
                                             :is-confirmation="isFinalSubmit(row.item)"
                                             class="d-block ml-auto mr-4"
                                             @click="submitGrade(row.item)"/>
          </b-col>
        </b-row>
      </template>
      <template v-slot:row-details="row">
        <base-details-container :key="getId(row.item)">

          <template v-slot:details-body>
            <proposal-show-details-row :proposal="row.item"/>
          </template>

          <template v-slot:details-footer>
            <base-collapse-block-button button-text="Hide details"
                                        @click="handleDetailsShowing(row)"/>
          </template>

        </base-details-container>
      </template>
    </b-table>

    <b-table-footer v-model="loadingFromProp"
                    :current-page="currentPage"
                    :data-length="proposalsFromProp.length"
                    :per-page="perPage"
                    @change-page="changePage">
      <template v-slot>
        <base-per-page-input v-model="perPage"/>
      </template>
    </b-table-footer>
    <b-modal id="gradeModal"
             :ref="modalRef"
             :title="isManagementEvaluation ?'Management evaluation form' : 'Final grade form'"
             bg-variant="light"
             header-bg-variant="dark"
             header-text-variant="white"
             hide-footer
             lazy
             size="full-response"
             @hidden="closeModal">
      <grade-modal v-model="proposal"
                   :isManagementEvaluation="isManagementEvaluation"
                   :isUserEditor="isUserEditor"
                   :shiftsCounterData="shiftsCounterData"
                   @close-modal="closeModalSlot"/>
    </b-modal>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import ActionOpenProposalFullDetails from '@/components/actions/ActionOpenProposalFullDetails';
  import BaseActionExpandDetails from '@/components/actions/BaseActionExpandDetails';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseCollapseBlockButton from '@/components/base-components/BaseCollapseBlockButton';
  import BaseDetailsContainer from '@/components/base-components/BaseDetailsContainer';
  import BaseDoubleClickSubmitButton from '@/components/base-components/BaseDoubleClickSubmitButton';
  import BaseMaterialDesignIconButton from '@/components/base-components/base-icons/BaseMaterialDesignIconButton';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import BTableFooter from '@/components/BTableFooter';
  import GradeModal from '@/components/evaluation/GradeModal';
  import ProposalFilter from '@/components/ProposalFilter';
  import ProposalShowDetailsRow from '@/components/evaluation/ProposalShowDetailsRow';
  import submitBtnOptions from '@/json/doubleClickSubmitButton';
  import TrafficLightsButtons from '@/components/traffic_lights_buttons';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMark';
  import { experiment } from '@/mixins/experiments';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import { openedDetails } from '@/mixins/openedDetails';
  import { underscoresToHyphens } from '@/helpers';

  export default {
    name: 'GradeTable',
    components: {
      ActionDownloadPdf,
      ActionOpenProposalFullDetails,
      BaseActionExpandDetails,
      BaseCollapseBlockButton,
      BaseColumnOrdinalNumber,
      BaseDetailsContainer,
      BaseDoubleClickSubmitButton,
      BaseMaterialDesignIconButton,
      BasePerPageInput,
      BTableFooter,
      ProposalShowDetailsRow,
      GradeModal,
      ProposalFilter,
      TrafficLightsButtons,
    },
    mixins: [experiment, infrastructureMixin, openedDetails],
    props: {
      gradeHelpers: {
        type: Object,
        required: true,
      },
      isManagementEvaluation: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      parent: {
        type: Number,
        default: 0,
      },
      proposals: {
        type: Array,
        default: () => [],
      },
      selectedCall: {
        type: String,
        default: '',
      },
      shiftsCounterData: {
        type: Object,
        default: () => ({}),
      },
      userElevatedPermissions: {
        type: Object,
        required: true,
        validator(property) {
          const requiredKeys = ['isEditor', 'isSubmitter'];
          const propertyKeys = Object.keys(property);

          const areRequiredKeysIncluded = requiredKeys.every((el) => propertyKeys.includes(el));
          const areRequiredValuesBoolean = requiredKeys.every((key) => typeof property[key] === 'boolean');
          return areRequiredKeysIncluded && areRequiredValuesBoolean;
        },
      },
    },
    data() {
      return {
        proposal: {},
        currentPage: 1,
        perPage: 20,
        proposalsFromProp: [],
        modalRef: 'modalRef',
        trafficLightsOptions,
        submitBtnOptions,
        finalSubmit: '',
      };
    },
    computed: {
      gradeType() {
        return this.isManagementEvaluation ? 'management_evaluation' : 'final_grade';
      },
      isUserEditor() {
        return this.userElevatedPermissions.isEditor;
      },
      gradeTypeUrl() {
        return underscoresToHyphens(this.gradeType);
      },
      loadingFromProp() {
        return this.loading;
      },
    },
    watch: {
      proposals() {
        // Update proposals when proposal filter input has value
        // Also empty the proposalsFromProp list when changing call (All calls option is exception)
        const renderedProposalsIds = this.getRenderedProposalsIds();
        this.updateRenderedProposals(renderedProposalsIds);

        this.updateProposalsOnInitializationOrCallChange();
      },
      proposalsFromProp: {
        handler(newValue) {
          this.$emit('filtered-proposals', newValue);
        },
      },
    },
    methods: {
      changePage(val) {
        this.currentPage = val;
      },
      closeModal() {
        const index = this.proposalsFromProp.findIndex((el) => this.getId(el) === this.getId(this.proposal));
        if (index >= 0) {
          this.$set(this.proposalsFromProp, index, this.proposal);
          this.$emit('update-proposal', this.proposal);
        }
      },
      editable(proposal) {
        if (proposal[this.gradeType].states) {
          return (proposal[this.gradeType].states.name !== 'submitted');
        }
        return true;
      },
      getRenderedProposalsIds() {
        return this.proposalsFromProp.map((proposal) => this.getId(proposal));
      },
      isEditionAllowed(proposal) {
        if (!this.userElevatedPermissions.isEditor) return false;
        return this.editable(proposal);
      },
      isFinalSubmit(proposal) {
        return this.getId(proposal) === this.finalSubmit;
      },
      setShowFinalSubmit(proposal, unusedIndex, event) {
        this.finalSubmit = '';
        if (event.target.id === this.submitBtnOptions.attempt.icon.id) {
          this.finalSubmit = this.getId(proposal);
        }
      },
      isSubmissionAllowed(proposal) {
        if (!this.userElevatedPermissions.isSubmitter) return false;
        return this.editable(proposal);
      },
      setProposal(proposal) {
        this.proposal = proposal;
      },
      sortCompare(aRow, bRow, key) {
        // Code based on b-table sortCompare example
        // By returning null sorting is passed to default function
        // Function sort by length of element or return null if equal
        if (['final_grade.rank', 'management_evaluation.rank'].includes(key)) {
          const columnGrade = key.split('.')[0];
          const a = aRow[columnGrade]?.rank || '';
          const b = bRow[columnGrade]?.rank || '';

          if ([a, b].includes('A*') || [a, b].includes('')) {
            const al = a.length;
            const bl = b.length;

            // eslint-disable-next-line no-nested-ternary
            return al < bl ? 1 : al > bl ? -1 : null;
          }
        }
        return null;
      },
      async submitGrade(item) {
        try {
          const proposalId = this.getId(item);
          const response = await this.axios.patch(
            `/documents/${proposalId}/${this.gradeTypeUrl}/${this.infrastructure}`,
          );
          // eslint-disable-next-line no-param-reassign
          item[this.gradeType].states.name = 'submitted';
          this.handleSetExperimentNotification(response.data.message);
        } catch (error) {
          if (error.response?.data?.error) {
            this.$notify({ type: 'error', title: error.response.data.error });
          } else {
            this.$notify({ type: 'error', title: 'Communication error' });
          }
        }
      },
      updateProposalsOnInitializationOrCallChange() {
        if (this.proposalsFromProp.length === 0 || this.selectedCall === 'All calls') {
          this.proposalsFromProp = this.proposals;
        }
      },
      updateRenderedProposals(renderedProposalsIds) {
        this.proposalsFromProp = this.proposals.filter(
          (proposal) => renderedProposalsIds.includes(this.getId(proposal)),
        );
      },
    },
  };
</script>

<style scoped>
  .default-actions {
    padding: 0;
    margin-left: 0.7rem;
  }
</style>
